import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Printer } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import DriverAutocomplete from '@/components/documents/DriverAutocomplete';

/**
 * Form untuk membuat batch pengiriman baru langsung dari dialog cetak.
 * Setelah simpan, cetak batch tsb via callback `onCreated(batchIndex)`.
 *
 * Props:
 *   - tx: transaksi induk
 *   - docType: 'so' | 'delivery-order' | 'invoice'
 *   - summary: hasil `_delivery_summary` per item
 *   - onCancel: batalkan form
 *   - onCreated(batchIndex): dipanggil setelah batch tersimpan
 */
export default function CreateAndPrintForm({ tx, docType, summary, onCancel, onCreated }) {
  const remaining = useMemo(() => summary.filter((s) => s.quantity_remaining > 0), [summary]);
  const initialQtys = useMemo(() => {
    const o = {};
    remaining.forEach((s) => { o[s.product_id] = s.quantity_remaining; });
    return o;
  }, [remaining]);
  const [qtys, setQtys] = useState(initialQtys);
  const [notes, setNotes] = useState('');
  const [driver, setDriver] = useState(tx.driver_name || '');
  const [plate, setPlate] = useState(tx.vehicle_plate || '');
  const [address, setAddress] = useState(tx.delivery_address || tx.customer_address || '');
  const [deliveryDate, setDeliveryDate] = useState(tx.delivery_date || new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const isSJ = docType === 'delivery-order';

  const totalQty = Object.values(qtys).reduce((a, v) => a + (Number(v) || 0), 0);

  const setQty = (pid, val) => {
    const max = (remaining.find((r) => r.product_id === pid) || {}).quantity_remaining || 0;
    const n = val === '' ? '' : Math.max(0, Math.min(max, Math.floor(Number(val) || 0)));
    setQtys((prev) => ({ ...prev, [pid]: n }));
  };

  const validate = () => {
    const items = Object.entries(qtys)
      .map(([product_id, q]) => ({ product_id, quantity: Number(q) || 0 }))
      .filter((x) => x.quantity > 0);
    if (items.length === 0) {
      toast.error('Isi minimal 1 item dengan qty > 0');
      return null;
    }
    if (isSJ && (!driver.trim() || !plate.trim() || !address.trim())) {
      toast.error('Untuk Surat Jalan, nama sopir, plat kendaraan, dan alamat pengiriman wajib');
      return null;
    }
    return items;
  };

  const submit = async () => {
    const items = validate();
    if (!items) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/transactions/${tx.id}/deliveries`, {
        items,
        notes: notes || null,
        driver_name: driver.trim() || null,
        vehicle_plate: plate.trim() || null,
        delivery_address: address.trim() || null,
        delivery_date: deliveryDate || null,
      });
      const batches = data.delivery_batches || [];
      const newBatch = batches.reduce((a, b) => (b.batch_index > (a?.batch_index || 0) ? b : a), null);
      toast.success(`Batch #${newBatch?.batch_index} tercatat, sedang mencetak...`);
      onCreated(newBatch?.batch_index);
    } catch (e) {
      console.error('[CreateAndPrintForm] gagal buat batch:', e);
      toast.error(e?.response?.data?.detail || 'Gagal buat batch');
    } finally { setSaving(false); }
  };

  const bulkAll = () => {
    const o = {};
    remaining.forEach((s) => { o[s.product_id] = s.quantity_remaining; });
    setQtys(o);
  };
  const bulkClear = () => setQtys({});

  return (
    <div className="rounded-md border p-3 space-y-2 bg-blue-50/40 border-blue-200">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-blue-900">Buat Batch Baru</div>
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={bulkAll}>Kirim Semua</Button>
          <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={bulkClear}>Kosongkan</Button>
        </div>
      </div>
      <div className="text-[10px] text-muted-foreground">Pilih berapa yang mau dicetak/dikirim di batch ini (default: semua sisa).</div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-xs">Barang</TableHead>
            <TableHead className="text-xs text-right">Sisa</TableHead>
            <TableHead className="text-xs text-right w-24">Kirim</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {remaining.map((s) => (
            <TableRow key={s.product_id}>
              <TableCell className="text-xs">
                <div className="font-medium">{s.name}</div>
                <div className="text-[10px] font-mono text-muted-foreground">{s.sku}</div>
              </TableCell>
              <TableCell className="text-xs text-right tabular-nums text-amber-700">{s.quantity_remaining}</TableCell>
              <TableCell>
                <Input
                  type="number" min="0" max={s.quantity_remaining} step="1"
                  value={qtys[s.product_id] ?? ''}
                  onChange={(e) => setQty(s.product_id, e.target.value)}
                  className="h-7 text-xs tabular-nums text-right"
                  data-testid={`print-batch-qty-${s.product_id}`}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Info pengiriman — untuk SJ wajib, untuk SO/Invoice opsional tapi selalu terisi
          supaya batch printout langsung tercetak lengkap. */}
      <DriverAutocomplete
        driver={driver}
        setDriver={setDriver}
        plate={plate}
        setPlate={setPlate}
        required={isSJ}
        testIdPrefix="print-batch-driver-ac"
      />
      <div>
        <Label className="text-xs">Alamat Pengiriman {isSJ && <span className="text-rose-600">*</span>}</Label>
        <Input value={address} onChange={(e) => setAddress(e.target.value)} className="h-7 text-xs" placeholder="Alamat lengkap tujuan" data-testid="print-batch-address" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Tanggal Kirim</Label>
          <Input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} className="h-7 text-xs" data-testid="print-batch-date" />
        </div>
        <div>
          <Label className="text-xs">Catatan (opsional)</Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="h-7 text-xs" placeholder="mis. Kirim pagi ini" data-testid="print-batch-notes" />
        </div>
      </div>

      <div className="flex items-center justify-between pt-1">
        <div className="text-xs text-muted-foreground">Total batch ini: <b className="tabular-nums text-foreground">{totalQty}</b> btg</div>
        <div className="flex gap-1">
          <Button variant="outline" size="sm" onClick={onCancel}>Batal</Button>
          <Button size="sm" onClick={submit} disabled={saving || totalQty === 0} data-testid="print-batch-create-save">
            <Printer className="h-3.5 w-3.5 mr-1" /> {saving ? 'Menyimpan...' : 'Buat & Cetak'}
          </Button>
        </div>
      </div>
    </div>
  );
}
